// ──────────────────────────────────────────────────────────────────────────
// DiarioStatisticheMatrice.js — Sotto-tab "📋 Matrici P/L".
//
// Implementazione v131 sub-deploy 2 (TASK 8 Fase 5).
// Endpoint backend: GET /diario/statistiche/matrice (Cloud Run 00156-fj4).
//
// Design v129 (D93) + decisioni v131:
//   - Righe: sistemi attivi (ordinati per n_op decrescente) + Varie + non più attivi
//   - Colonne: 12 mesi della stagione (ago→lug oppure dal mese inizio fino a oggi)
//   - Heatmap: 8 livelli da rosso scuro (perdita forte) a verde scuro (guadagno),
//              calibrata sui min/max effettivi
//   - Switch € / % (D93)
//   - Toggle "Nascondi sistemi senza operazioni" (default OFF, D93)
//   - Drill-down su click cella con valore: popup con lista operazioni
//     del sistema in quel mese (Decisione 5 v131)
//   - Cella "—" non cliccabile (Decisione 5 v131)
// ──────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useMemo } from "react";
import { useStatisticheFiltri } from "../contexts/StatisticheFiltriContext";
import { fmtEur, fmtPct, fmtNum, fmtData } from "../components/stats/statsFormat";
import BannerContesto from "../components/stats/BannerContesto";

import { API } from "../config";
const FONT = "'DM Sans', sans-serif";
const MONO = "'IBM Plex Mono', monospace";

// ── Heatmap a 8 livelli ───────────────────────────────────────────────────
// Palette divergente rosso (perdita) → bianco (zero) → verde (guadagno).
// Le soglie sono calibrate sui min/max effettivi della stagione.
//
// Livelli per le perdite (4): rosso scuro -> rosa
// Livelli per i guadagni (4): verde chiaro -> verde scuro
// Cella zero: bianco / grigino

const PALETTE_NEGATIVA = [
  "#7F1D1D", // -4 (più negativo)
  "#B91C1C", // -3
  "#EF4444", // -2
  "#FCA5A5", // -1
];
const PALETTE_POSITIVA = [
  "#A7F3D0", // +1
  "#34D399", // +2
  "#10B981", // +3
  "#047857", // +4 (più positivo)
];
const COLORE_ZERO = "#F8FAFC";

// Determina colore di sfondo per cella in base al valore P/L €
function coloreCella(plEur, minEur, maxEur) {
  if (plEur === 0) return COLORE_ZERO;
  if (plEur > 0) {
    if (maxEur <= 0) return COLORE_ZERO;
    const ratio = plEur / maxEur;
    const idx = Math.min(3, Math.floor(ratio * 4));
    return PALETTE_POSITIVA[idx];
  }
  if (minEur >= 0) return COLORE_ZERO;
  const ratio = plEur / minEur; // entrambi negativi -> ratio in [0,1]
  const idx = Math.min(3, Math.floor(ratio * 4));
  return PALETTE_NEGATIVA[idx];
}

// Determina colore testo (bianco su sfondi scuri, scuro su chiari)
function coloreTesto(plEur, minEur, maxEur) {
  if (plEur === 0) return "#94A3B8";
  if (plEur > 0) {
    if (maxEur <= 0) return "#1E293B";
    const ratio = plEur / maxEur;
    return ratio > 0.5 ? "#fff" : "#064E3B";
  }
  if (minEur >= 0) return "#1E293B";
  const ratio = plEur / minEur;
  return ratio > 0.5 ? "#fff" : "#7F1D1D";
}

// ── Etichette mesi (YYYY-MM -> "Lug 25") ─────────────────────────────────

const MESI_NOMI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

function etichettaMese(yyyymm) {
  const [y, m] = yyyymm.split("-");
  return `${MESI_NOMI[parseInt(m, 10) - 1]} ${y.slice(2)}`;
}

// ── Popup drill-down cella (sistema × mese) ─────────────────────────────

function PopupDettaglioCella({ riga, mese, switchValore, onClose }) {
  const [operazioni, setOperazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState(null);
  const filtri = useStatisticheFiltri();

  // Calcolo da/a per il mese specifico
  const [annoStr, meseStr] = mese.split("-");
  const annoNum = parseInt(annoStr, 10);
  const meseNum = parseInt(meseStr, 10);
  const daData = `${annoStr}-${meseStr}-01`;
  const ultimoGiorno = new Date(annoNum, meseNum, 0).getDate();
  const aData = `${annoStr}-${meseStr}-${String(ultimoGiorno).padStart(2, "0")}`;

  useEffect(() => {
    let annullato = false;
    setLoading(true);
    setErrore(null);

    const params = new URLSearchParams();
    params.set("da_data", daData);
    params.set("a_data", aData);
    if (riga.tipo_riga === "sistema" && riga.sistema_id) {
      params.set("sistema_id", riga.sistema_id);
    }

    fetch(`${API}/diario/operazioni?${params.toString()}`)
      .then(r => {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(data => {
        if (annullato) return;
        const ops = Array.isArray(data) ? data : [];
        let filtrate = ops;
        if (riga.tipo_riga === "varie") {
          filtrate = filtrate.filter(o => !o.sistema_id);
        } else if (riga.tipo_riga === "sistema") {
          filtrate = filtrate.filter(o => o.sistema_id === riga.sistema_id);
        }
        // Filtro client-side per tipo_punta_banca se attivo globalmente
        if (filtri.queryParams.tipo_punta_banca) {
          filtrate = filtrate.filter(o => o.tipo_punta_banca === filtri.queryParams.tipo_punta_banca);
        }
        // Tengo solo le chiuse (la cella della matrice è basata su chiuse)
        filtrate = filtrate.filter(o => o.esito_operazione !== null && o.esito_operazione !== undefined);
        setOperazioni(filtrate);
      })
      .catch(e => {
        if (!annullato) setErrore("Impossibile caricare le operazioni: " + e.message);
      })
      .finally(() => {
        if (!annullato) setLoading(false);
      });

    return () => { annullato = true; };
  }, [riga, mese, daData, aData, filtri.queryParams.tipo_punta_banca]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Trova la cella corrispondente in riga.celle (per mostrare totale)
  const cella = riga.celle?.find(c => c.mese === mese);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(15, 23, 42, 0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12,
          width: "min(900px, 100%)", maxHeight: "85vh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)", fontFamily: FONT,
        }}
      >
        <div style={{
          padding: "16px 20px", borderBottom: "1px solid #E2E8F0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#1E293B" }}>
              {riga.nome_sistema} · {etichettaMese(mese)}
            </div>
            {cella && (
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                {cella.n_op} operazion{cella.n_op === 1 ? "e" : "i"} chiuse · P/L{" "}
                <span style={{
                  color: cella.pl_eur > 0 ? "#16A34A" : (cella.pl_eur < 0 ? "#DC2626" : "#64748B"),
                  fontWeight: 600, fontFamily: MONO,
                }}>
                  {switchValore === "eur" ? fmtEur(cella.pl_eur) : fmtPct(cella.pl_pct)}
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            style={{
              border: "none", background: "transparent",
              fontSize: 22, color: "#94A3B8", cursor: "pointer",
              width: 32, height: 32, borderRadius: 6,
            }}
          >×</button>
        </div>

        <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
          {loading && (
            <div style={{ textAlign: "center", color: "#94A3B8", padding: 40 }}>
              Caricamento operazioni...
            </div>
          )}
          {errore && (
            <div style={{
              background: "#FEF2F2", border: "1px solid #FECACA",
              color: "#991B1B", borderRadius: 8, padding: 12, fontSize: 13,
            }}>{errore}</div>
          )}
          {!loading && !errore && operazioni.length === 0 && (
            <div style={{ textAlign: "center", color: "#94A3B8", padding: 40, fontSize: 13 }}>
              Nessuna operazione chiusa in questo periodo.
            </div>
          )}
          {!loading && !errore && operazioni.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#F8FAFC" }}>
                  {[
                    { label: "Data", align: "right" },
                    { label: "Partita", align: "left" },
                    { label: "Mercato", align: "left" },
                    { label: "Tipo", align: "right" },
                    { label: "Stake", align: "right" },
                    { label: "Quota", align: "right" },
                    { label: "Esito", align: "right" },
                    { label: "P/L", align: "right" },
                  ].map((c, i) => (
                    <th key={i} style={{
                      textAlign: c.align,
                      padding: "8px 10px", fontSize: 11, fontWeight: 600,
                      color: "#64748B", borderBottom: "1px solid #E2E8F0",
                      textTransform: "uppercase", letterSpacing: 0.4,
                    }}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {operazioni.map(o => {
                  const stileTd = {
                    padding: "8px 10px", borderBottom: "1px solid #F1F5F9",
                    fontSize: 12, color: "#1E293B",
                  };
                  const stileTdNum = { ...stileTd, fontFamily: MONO, textAlign: "right", whiteSpace: "nowrap" };
                  const esitoColore = {
                    "Vinta": "#16A34A", "Persa": "#DC2626", "Void": "#64748B",
                  };
                  const pl = o.profitto_netto_eur;
                  const colorePl = pl > 0 ? "#16A34A" : (pl < 0 ? "#DC2626" : "#64748B");
                  return (
                    <tr key={o.id}>
                      <td style={stileTdNum}>{fmtData(o.data_partita)}</td>
                      <td style={stileTd}>
                        <div style={{ fontWeight: 500 }}>{o.casa} – {o.trasferta}</div>
                        {o.competizione && (
                          <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>{o.competizione}</div>
                        )}
                      </td>
                      <td style={stileTd}>{o.mercato || "—"}</td>
                      <td style={{ ...stileTd, textAlign: "right" }}>
                        <span style={{
                          background: o.tipo_punta_banca === "Punta" ? "#DBEAFE" : "#FEE4E2",
                          color: o.tipo_punta_banca === "Punta" ? "#1E40AF" : "#9F1239",
                          borderRadius: 999, padding: "2px 8px", fontSize: 11, fontWeight: 600,
                        }}>{o.tipo_punta_banca || "—"}</span>
                      </td>
                      <td style={stileTdNum}>{o.size_eur_snapshot != null ? fmtEur(o.size_eur_snapshot) : "—"}</td>
                      <td style={stileTdNum}>{o.quota_effettiva != null ? fmtNum(o.quota_effettiva, 2) : "—"}</td>
                      <td style={{ ...stileTd, textAlign: "right" }}>
                        <span style={{
                          color: esitoColore[o.esito_operazione] || "#64748B",
                          fontWeight: 600, fontSize: 11,
                        }}>{o.esito_operazione}</span>
                      </td>
                      <td style={{ ...stileTdNum, color: colorePl, fontWeight: 600 }}>
                        {pl != null ? fmtEur(pl) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Componente principale ────────────────────────────────────────────────

export default function DiarioStatisticheMatrice() {
  const filtri = useStatisticheFiltri();
  const [datiBackend, setDatiBackend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState(null);
  const [switchValore, setSwitchValore] = useState("eur"); // "eur" | "pct"
  const [nascondiSenzaOp, setNascondiSenzaOp] = useState(false);
  const [popupCella, setPopupCella] = useState(null); // { riga, mese }

  useEffect(() => {
    let annullato = false;
    setLoading(true);
    setErrore(null);

    const params = new URLSearchParams();
    Object.entries(filtri.queryParams).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") params.set(k, v);
    });

    fetch(`${API}/diario/statistiche/matrice?${params.toString()}`)
      .then(r => {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(data => {
        if (!annullato) setDatiBackend(data);
      })
      .catch(e => {
        if (!annullato) setErrore("Errore caricamento: " + e.message);
      })
      .finally(() => {
        if (!annullato) setLoading(false);
      });

    return () => { annullato = true; };
  }, [filtri.queryParams]);

  // Costruzione righe finali (toggle + ordinamento già da BE)
  const righeFinal = useMemo(() => {
    if (!datiBackend || !datiBackend.righe) return [];
    let righe = datiBackend.righe;
    if (nascondiSenzaOp) {
      righe = righe.filter(r => {
        if (r.tipo_riga === "totale" || r.tipo_riga === "varie") return true;
        if (r.non_piu_attivo) return true;
        // Riga ha almeno una cella con n_op > 0?
        return r.celle.some(c => c.n_op > 0);
      });
    }
    // Se sistema_id filtrato: porto la riga match in cima sotto al TOTALE
    if (filtri.sistema_id) {
      const tot = righe.find(r => r.tipo_riga === "totale");
      const match = righe.find(r => r.tipo_riga === "sistema" && r.sistema_id === filtri.sistema_id);
      const altri = righe.filter(r => r !== tot && r !== match);
      const finale = [];
      if (tot) finale.push(tot);
      if (match) finale.push(match);
      finale.push(...altri);
      return finale;
    }
    return righe;
  }, [datiBackend, nascondiSenzaOp, filtri.sistema_id]);

  const nSenzaOp = useMemo(() => {
    if (!datiBackend || !datiBackend.righe) return 0;
    return datiBackend.righe.filter(r =>
      r.tipo_riga === "sistema" && !r.non_piu_attivo &&
      r.celle.every(c => c.n_op === 0)
    ).length;
  }, [datiBackend]);

  // ── Stati di loading/errore/vuoto ────────────────────────────────────

  if (loading && !datiBackend) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#94A3B8", fontFamily: FONT }}>
        Caricamento dati...
      </div>
    );
  }

  if (errore) {
    return (
      <BannerContesto
        messaggio={errore}
        azioni={[{ label: "Riprova", onClick: () => window.location.reload() }]}
      />
    );
  }

  if (!datiBackend || !datiBackend.stagione) {
    return (
      <BannerContesto
        messaggio="Nessuna stagione disponibile. Crea una stagione dalla tab Capitale per iniziare."
      />
    );
  }

  if (datiBackend.gran_totale.n_op === 0) {
    return (
      <>
        {filtri.hasFiltriAttivi ? (
          <BannerContesto
            messaggio="Nessuna operazione corrisponde ai filtri attivi nel periodo selezionato."
            azioni={[{ label: "Reset filtri", onClick: filtri.resetTutti }]}
          />
        ) : (
          <BannerContesto
            messaggio="Non hai ancora registrato operazioni in questa stagione. Aggiungile dalla tab Diario."
          />
        )}
      </>
    );
  }

  const mesi = datiBackend.mesi || [];
  const minEur = datiBackend.heatmap?.pl_eur_min || 0;
  const maxEur = datiBackend.heatmap?.pl_eur_max || 0;

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div style={{ fontFamily: FONT }}>
      {/* Toolbar: switch € / % + toggle nascondi */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 10, gap: 12, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 12, color: "#64748B" }}>Mostra valori in:</span>
          {/* Toggle €/%: pulsanti corti con tooltip esplicativo on hover */}
          <span style={{
            display: "inline-flex", background: "#eef2f7",
            borderRadius: 6, padding: 2,
          }}>
            <button
              type="button"
              onClick={() => setSwitchValore("eur")}
              title="Valore assoluto in euro"
              style={{
                background: switchValore === "eur" ? "#fff" : "transparent",
                color: switchValore === "eur" ? "#1f2a37" : "#6b7280",
                border: switchValore === "eur" ? "1px solid #d6dde6" : "1px solid transparent",
                borderRadius: 4, padding: "2px 10px",
                fontSize: 12, fontWeight: switchValore === "eur" ? 600 : 400,
                cursor: "pointer", lineHeight: 1.4, fontFamily: FONT,
              }}
            >€</button>
            <button
              type="button"
              onClick={() => setSwitchValore("pct")}
              title="Percentuale sul capitale di inizio stagione"
              style={{
                background: switchValore === "pct" ? "#fff" : "transparent",
                color: switchValore === "pct" ? "#1f2a37" : "#6b7280",
                border: switchValore === "pct" ? "1px solid #d6dde6" : "1px solid transparent",
                borderRadius: 4, padding: "2px 10px",
                fontSize: 12, fontWeight: switchValore === "pct" ? 600 : 400,
                cursor: "pointer", lineHeight: 1.4, fontFamily: FONT,
              }}
            >%</button>
          </span>
        </div>
        <label style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          fontSize: 12, color: "#475569", cursor: "pointer",
        }}>
          <input
            type="checkbox"
            checked={nascondiSenzaOp}
            onChange={(e) => setNascondiSenzaOp(e.target.checked)}
            style={{ cursor: "pointer" }}
          />
          Nascondi sistemi senza operazioni
          {nascondiSenzaOp && nSenzaOp > 0 && (
            <span style={{ color: "#94A3B8", fontSize: 11 }}>
              ({nSenzaOp} nascost{nSenzaOp === 1 ? "o" : "i"})
            </span>
          )}
        </label>
      </div>

      {/* Matrice */}
      <div style={{
        background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10,
        overflow: "hidden", overflowX: "auto",
      }}>
        <table style={{
          width: "100%", borderCollapse: "collapse", fontFamily: FONT,
          minWidth: 800,
        }}>
          <thead>
            <tr style={{ background: "#fff" }}>
              <th style={{
                position: "sticky", left: 0, zIndex: 2, background: "#fff",
                padding: "12px 12px", fontSize: 11, fontWeight: 600,
                color: "#64748B", textAlign: "left",
                textTransform: "uppercase", letterSpacing: 0.5,
                borderBottom: "2px solid #E2E8F0",
                borderRight: "1px solid #E2E8F0",
                whiteSpace: "nowrap", minWidth: 200,
              }}>Sistema</th>
              {mesi.map(m => (
                <th key={m} style={{
                  padding: "12px 8px", fontSize: 11, fontWeight: 600,
                  color: "#64748B", textAlign: "center",
                  textTransform: "uppercase", letterSpacing: 0.5,
                  borderBottom: "2px solid #E2E8F0",
                  background: "#fff",
                  whiteSpace: "nowrap",
                }}>{etichettaMese(m)}</th>
              ))}
              <th style={{
                padding: "12px 12px", fontSize: 11, fontWeight: 700,
                color: "#1E293B", textAlign: "right",
                textTransform: "uppercase", letterSpacing: 0.5,
                borderBottom: "2px solid #E2E8F0",
                borderLeft: "1px solid #E2E8F0",
                whiteSpace: "nowrap", background: "#fff",
              }}>Tot.</th>
            </tr>
          </thead>
          <tbody>
            {righeFinal.map((riga, idxR) => {
              const isTot = riga.tipo_riga === "totale";
              const isVarie = riga.tipo_riga === "varie";
              const isEvidenziato = !isTot && riga.sistema_id && filtri.sistema_id === riga.sistema_id;
              // Pattern Diario: sfondo bianco. TOTALE: grassetto.
              // Evidenziato: barra azzurra a sinistra (nella sticky col Sistema).
              const sfondoNormale = "#fff";
              const bordoInferiore = isTot
                ? "2px solid #E2E8F0"
                : "1px solid #F1F5F9";
              return (
                <tr
                  key={`${riga.tipo_riga}-${riga.sistema_id || "varie"}-${idxR}`}
                  style={{ background: sfondoNormale }}
                >
                  <td style={{
                    position: "sticky", left: 0, zIndex: 1,
                    background: sfondoNormale,
                    padding: "12px 12px", fontSize: 13,
                    color: "#1E293B",
                    fontWeight: isTot ? 700 : 500,
                    borderRight: "1px solid #E2E8F0",
                    borderBottom: bordoInferiore,
                    borderLeft: isEvidenziato ? "3px solid #2563EB" : "3px solid transparent",
                    paddingLeft: isEvidenziato ? 9 : 12,
                    whiteSpace: "nowrap",
                  }}>
                    {riga.nome_sistema}
                    {riga.non_piu_attivo && (
                      <span style={{
                        marginLeft: 8, fontSize: 10, color: "#94A3B8", fontStyle: "italic",
                      }}>non più attivo</span>
                    )}
                  </td>
                  {mesi.map(m => {
                    const cella = riga.celle.find(c => c.mese === m);
                    if (!cella) return <td key={m}></td>;
                    const haDati = cella.n_op > 0;
                    const valore = switchValore === "eur" ? cella.pl_eur : cella.pl_pct;
                    const sfondo = haDati ? coloreCella(cella.pl_eur, minEur, maxEur) : "transparent";
                    const colore = haDati ? coloreTesto(cella.pl_eur, minEur, maxEur) : "#CBD5E1";
                    const cliccabile = haDati && !isTot;
                    return (
                      <td
                        key={m}
                        onClick={cliccabile ? () => setPopupCella({ riga, mese: m }) : undefined}
                        style={{
                          padding: "12px 8px", fontSize: 12, fontFamily: MONO,
                          textAlign: "center",
                          background: sfondo, color: colore,
                          fontWeight: isTot ? 700 : (haDati ? 600 : 400),
                          borderBottom: bordoInferiore,
                          cursor: cliccabile ? "pointer" : "default",
                          whiteSpace: "nowrap",
                          transition: "filter 0.1s",
                        }}
                        onMouseEnter={cliccabile ? (e) => {
                          e.currentTarget.style.filter = "brightness(1.08)";
                        } : undefined}
                        onMouseLeave={cliccabile ? (e) => {
                          e.currentTarget.style.filter = "none";
                        } : undefined}
                        title={cliccabile
                          ? `${cella.n_op} operazion${cella.n_op === 1 ? "e" : "i"} - click per dettaglio`
                          : ""}
                      >
                        {haDati
                          ? (switchValore === "eur" ? fmtEur(valore) : fmtPct(valore))
                          : "—"}
                      </td>
                    );
                  })}
                  <td style={{
                    padding: "12px 12px", fontSize: 13, fontFamily: MONO,
                    textAlign: "right",
                    fontWeight: 700,
                    color: riga.totale_eur > 0 ? "#16A34A" : (riga.totale_eur < 0 ? "#DC2626" : "#64748B"),
                    background: sfondoNormale,
                    borderLeft: "1px solid #E2E8F0",
                    borderBottom: bordoInferiore,
                    whiteSpace: "nowrap",
                  }}>
                    {switchValore === "eur" ? fmtEur(riga.totale_eur) : fmtPct(riga.totale_pct)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legenda heatmap */}
      <div style={{
        marginTop: 12, padding: "10px 14px",
        background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: "#64748B", fontWeight: 600 }}>Heatmap:</span>
          <span style={{ fontSize: 11, color: "#7F1D1D" }}>{fmtEur(minEur)}</span>
          <div style={{ display: "inline-flex", borderRadius: 4, overflow: "hidden", border: "1px solid #E2E8F0" }}>
            {PALETTE_NEGATIVA.map((c, i) => (
              <div key={`n${i}`} style={{ width: 18, height: 14, background: c }} />
            ))}
            <div style={{ width: 18, height: 14, background: COLORE_ZERO }} />
            {PALETTE_POSITIVA.map((c, i) => (
              <div key={`p${i}`} style={{ width: 18, height: 14, background: c }} />
            ))}
          </div>
          <span style={{ fontSize: 11, color: "#047857" }}>{fmtEur(maxEur)}</span>
        </div>
        <div style={{ fontSize: 11, color: "#94A3B8", fontStyle: "italic" }}>
          Click su una cella con valore per vedere il dettaglio operazioni.
        </div>
      </div>

      {/* Popup drill-down cella */}
      {popupCella && (
        <PopupDettaglioCella
          riga={popupCella.riga}
          mese={popupCella.mese}
          switchValore={switchValore}
          onClose={() => setPopupCella(null)}
        />
      )}
    </div>
  );
}
